import { AuthError, Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { PasswordValidationResult, validatePassword } from './passwordPolicy';
import { supabase } from './supbaseClient';

// Auth context interface
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
    emailRedirectUrl?: string
  ) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: { [key: string]: any }) => Promise<{ error: AuthError | null }>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event: any, session: Session | null) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Get redirect URL based on environment
  const getRedirectUrl = () => {
    const nodeEnv = process.env.REACT_APP_NODE_ENV;
    switch (nodeEnv) {
      case 'staging':
        return 'https://orange-bush-09e248e1e.5.azurestaticapps.net';
      case 'prod':
        return 'https://app.notewell.ai';
      case 'dev':
        return 'http://localhost:3000';
      default:
        return 'http://localhost:3000';
    }
  };

  // Sign up function
  const signUp = async (
    email: string,
    password: string,
    displayName?: string,
    emailRedirectUrl?: string
  ) => {
    try {
      // Check if the password is valid before signing up the user
      const valdiationResult: PasswordValidationResult = validatePassword(password);
      if (!valdiationResult.ok) {
        return {
          user: null,
          error: {
            message: valdiationResult.error,
            status: 400,
            name: 'AuthError'
          } as AuthError
        };
      }

      let redirectUrl = `${getRedirectUrl()}/auth/callback`;
      // If emailRedirectUrl is provided, append it as a query parameter
      if (emailRedirectUrl) {
        redirectUrl += `?redirect=${encodeURIComponent(emailRedirectUrl)}`;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          ...(displayName ? { data: { display_name: displayName } } : {})
        }
      });

      if (error) {
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (error) {
      return {
        user: null,
        error: {
          message: 'An unexpected error occurred during sign up',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (error) {
      return {
        user: null,
        error: {
          message: 'An unexpected error occurred during sign in',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      queryClient.clear();
      return { error };
    } catch (error) {
      return {
        error: {
          message: 'An unexpected error occurred during sign out',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = getRedirectUrl();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${redirectUrl}/reset-password`
      });
      return { error };
    } catch (error) {
      return {
        error: {
          message: 'An unexpected error occurred while resetting password',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  // Update password function
  const updatePassword = async (password: string) => {
    try {
      // Check if the password is valid before updating
      const validationResult: PasswordValidationResult = validatePassword(password);
      if (!validationResult.ok) {
        return {
          error: {
            message: validationResult.error,
            status: 400,
            name: 'AuthError'
          } as AuthError
        };
      }

      const { error } = await supabase.auth.updateUser({
        password
      });
      return { error };
    } catch (error) {
      return {
        error: {
          message: 'An unexpected error occurred while updating password',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  // Update profile function
  const updateProfile = async (updates: { [key: string]: any }) => {
    try {
      const { error } = await supabase.auth.updateUser(updates);
      return { error };
    } catch (error) {
      return {
        error: {
          message: 'An unexpected error occurred while updating profile',
          status: 500,
          name: 'AuthError'
        } as AuthError
      };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
